import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { lumi } from "@/lib/lumi"
import { useAuth } from "@/hooks/useAuth"
import {ArrowLeft, Send, Loader2, Paperclip, X} from 'lucide-react'
import { Link } from "react-router-dom"
import toast from "react-hot-toast"

const categories = [
  { value: "poste_de_travail", label: "Problème poste de travail" },
  { value: "email", label: "Problème email" },
  { value: "imprimante", label: "Problème imprimante" },
  { value: "reseau_internet", label: "Problème réseau / internet" },
  { value: "acces_mot_de_passe", label: "Accès / mot de passe" },
  { value: "serveur_infrastructure", label: "Serveur / infrastructure" },
  { value: "sauvegarde", label: "Sauvegarde" },
  { value: "configuration", label: "Demande de configuration" },
  { value: "autre", label: "Autre" },
]

const priorities = [
  { value: "faible", label: "Faible", desc: "Pas d'impact immédiat" },
  { value: "normale", label: "Normale", desc: "Impact limité" },
  { value: "haute", label: "Haute", desc: "Impact important" },
  { value: "urgente", label: "Urgente", desc: "Blocage critique" },
]

export default function CreateTicket() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")
  const [files, setFiles] = useState<File[]>([])
  const [form, setForm] = useState({
    subject: "",
    category: "poste_de_travail",
    priority: "normale",
    description: "",
    company: "",
    clientPhone: "",
  })

  const updateForm = (key: string, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  const handleFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files)
      setFiles((prev) => [...prev, ...newFiles].slice(0, 10))
    }
  }

  const removeFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index))
  }

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.subject || !form.description) {
      return setError("Veuillez remplir tous les champs obligatoires.")
    }
    setError("")
    setLoading(true)

    try {
      // 1. Upload des pièces jointes si nécessaire
      let attachmentUrls: string[] = []
      if (files.length > 0) {
        try {
          const results = await lumi.tools.file.upload(files)
          attachmentUrls = results.filter((r: any) => r.fileUrl).map((r: any) => r.fileUrl)
          console.log("Fichiers uploadés:", attachmentUrls.length)
        } catch (uploadErr: any) {
          console.error("Erreur upload fichiers:", uploadErr)
          // On continue même si l'upload échoue
        }
      }

      // 2. Créer le ticket et notifier via la Deno function
      // La Deno function gère à la fois l'insertion en base ET l'envoi de l'email
      console.log("Envoi du ticket via la Deno function notify-admin...")

      const result = await lumi.functions.invoke("notify-admin", {
        method: "POST",
        body: {
          type: "ticket",
          data: {
            subject: form.subject,
            category: form.category,
            priority: form.priority,
            description: form.description,
            clientName: user?.userName || "Client",
            clientEmail: user?.email || "",
            clientPhone: form.clientPhone || "",
            company: form.company || "",
            attachments: attachmentUrls,
            creator: user?.userId || "",
            adminEmail: "contact@ljj-it.ch",
          },
        },
      })

      console.log("Résultat Deno function:", result)
      toast.success("Ticket créé avec succès !")
      navigate("/espace-client")
    } catch (err: any) {
      console.error("Erreur création ticket:", err)
      const msg = err?.data?.error || err?.message || "Erreur lors de la création du ticket."
      setError(msg)
      toast.error(msg)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen pt-24 lg:pt-32 pb-20">
      <div className="section-padding max-w-3xl mx-auto">
        <Link to="/espace-client" className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-cyan-400 transition-colors mb-6">
          <ArrowLeft className="w-4 h-4" />
          Retour au tableau de bord
        </Link>

        <h1 className="text-2xl lg:text-3xl font-bold text-white mb-2 animate-fade-in-up">
          Créer un <span className="gradient-text">nouveau ticket</span>
        </h1>
        <p className="text-slate-400 text-sm mb-8">Décrivez votre problème et nous vous répondrons rapidement.</p>

        <form onSubmit={submit} className="card !p-8 space-y-6 animate-fade-in-up" style={{ animationDelay: "0.1s" }}>
          {error && (
            <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
              {error}
            </div>
          )}

          {/* Objet */}
          <div>
            <label className="label-field">Objet de la demande *</label>
            <input
              type="text"
              value={form.subject}
              onChange={(e) => updateForm("subject", e.target.value)}
              placeholder="Ex: Impossible d'accéder à ma messagerie"
              className="input-field"
              required
            />
          </div>

          {/* Société + Téléphone */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="label-field">Société</label>
              <input
                type="text"
                value={form.company}
                onChange={(e) => updateForm("company", e.target.value)}
                placeholder="Nom de votre entreprise"
                className="input-field"
              />
            </div>
            <div>
              <label className="label-field">Téléphone</label>
              <input
                type="tel"
                value={form.clientPhone}
                onChange={(e) => updateForm("clientPhone", e.target.value)}
                placeholder="+41 79 123 45 67"
                className="input-field"
              />
            </div>
          </div>

          {/* Catégorie + Priorité */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="label-field">Catégorie *</label>
              <select
                value={form.category}
                onChange={(e) => updateForm("category", e.target.value)}
                className="input-field"
              >
                {categories.map((c) => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="label-field">Priorité *</label>
              <select
                value={form.priority}
                onChange={(e) => updateForm("priority", e.target.value)}
                className="input-field"
              >
                {priorities.map((p) => (
                  <option key={p.value} value={p.value}>{p.label} - {p.desc}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="label-field">Description détaillée *</label>
            <textarea
              value={form.description}
              onChange={(e) => updateForm("description", e.target.value)}
              placeholder="Décrivez votre problème en détail : quand est-il apparu, que se passe-t-il exactement, quels messages d'erreur voyez-vous..."
              className="input-field min-h-[160px] resize-y"
              required
            />
          </div>

          {/* Pièces jointes */}
          <div>
            <label className="label-field">Pièces jointes</label>
            <div className="border border-dashed border-slate-700 rounded-lg p-4 text-center hover:border-cyan-500/50 transition-colors">
              <input
                type="file"
                multiple
                onChange={handleFiles}
                className="hidden"
                id="file-upload"
                accept="image/*,.pdf,.doc,.docx,.txt,.zip"
              />
              <label htmlFor="file-upload" className="cursor-pointer">
                <Paperclip className="w-6 h-6 text-slate-500 mx-auto mb-2" />
                <p className="text-sm text-slate-400">
                  Cliquez pour ajouter des fichiers
                </p>
                <p className="text-xs text-slate-600 mt-1">Images, PDF, documents (max 10 Mo / fichier)</p>
              </label>
            </div>
            {files.length > 0 && (
              <div className="mt-3 space-y-2">
                {files.map((file, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm text-slate-300 bg-slate-800/50 rounded-lg px-3 py-2">
                    <Paperclip className="w-3.5 h-3.5 text-slate-500" />
                    <span className="flex-1 truncate">{file.name}</span>
                    <span className="text-xs text-slate-500">{(file.size / 1024).toFixed(0)} Ko</span>
                    <button type="button" onClick={() => removeFile(i)} className="text-slate-500 hover:text-red-400">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <button
            type="submit"
            disabled={loading}
            className="btn-primary w-full justify-center !py-3.5 disabled:opacity-50"
          >
            {loading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <>
                <Send className="w-4 h-4" />
                Envoyer le ticket
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  )
}
